import { useState } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { useTheme } from '../config/theme';
import { useLanguage } from '../config/language';
import { 
  Search, Car, ChevronLeft, ChevronRight, ArrowLeft, ArrowRight, 
  Settings, Cog, Gauge, Lightbulb, Wind, Disc, Battery, 
  Droplets, ThermometerSun, Radio, Shield, Sofa, PaintBucket,
  Wrench, CircleDot, Zap, Fan
} from 'lucide-react';

// Parts categories with icons
const partsCategories = [
  { id: 'engine', name: 'Engine Parts', nameAr: 'قطع المحرك', icon: Cog, color: 'bg-red-500', items: 156 },
  { id: 'brakes', name: 'Brakes & Discs', nameAr: 'الفرامل والديسكات', icon: Disc, color: 'bg-orange-500', items: 89 },
  { id: 'suspension', name: 'Suspension', nameAr: 'نظام التعليق', icon: Settings, color: 'bg-yellow-500', items: 124 },
  { id: 'electrical', name: 'Electrical', nameAr: 'الكهرباء', icon: Zap, color: 'bg-blue-500', items: 203 },
  { id: 'battery', name: 'Battery & Charging', nameAr: 'البطارية والشحن', icon: Battery, color: 'bg-green-500', items: 45 },
  { id: 'lights', name: 'Lights & Lamps', nameAr: 'الإضاءة والمصابيح', icon: Lightbulb, color: 'bg-amber-500', items: 167 },
  { id: 'ac', name: 'AC & Cooling', nameAr: 'التكييف والتبريد', icon: Wind, color: 'bg-cyan-500', items: 78 },
  { id: 'radiator', name: 'Radiator & Cooling', nameAr: 'الرديتر والتبريد', icon: ThermometerSun, color: 'bg-teal-500', items: 56 },
  { id: 'oil', name: 'Oils & Fluids', nameAr: 'الزيوت والسوائل', icon: Droplets, color: 'bg-purple-500', items: 89 },
  { id: 'filters', name: 'Filters', nameAr: 'الفلاتر', icon: Fan, color: 'bg-indigo-500', items: 67 },
  { id: 'exhaust', name: 'Exhaust System', nameAr: 'نظام العادم', icon: CircleDot, color: 'bg-gray-500', items: 45 },
  { id: 'transmission', name: 'Transmission', nameAr: 'ناقل الحركة', icon: Gauge, color: 'bg-pink-500', items: 134 },
  { id: 'steering', name: 'Steering', nameAr: 'نظام التوجيه', icon: CircleDot, color: 'bg-rose-500', items: 78 },
  { id: 'body', name: 'Body Parts', nameAr: 'قطع الهيكل', icon: Car, color: 'bg-slate-500', items: 245 },
  { id: 'interior', name: 'Interior', nameAr: 'الداخلية', icon: Sofa, color: 'bg-emerald-500', items: 189 },
  { id: 'audio', name: 'Audio & Electronics', nameAr: 'الصوتيات والإلكترونيات', icon: Radio, color: 'bg-violet-500', items: 112 },
  { id: 'safety', name: 'Safety & Airbags', nameAr: 'السلامة والوسائد', icon: Shield, color: 'bg-lime-500', items: 34 },
  { id: 'paint', name: 'Paint & Body Care', nameAr: 'الدهان والعناية', icon: PaintBucket, color: 'bg-fuchsia-500', items: 78 },
  { id: 'tools', name: 'Tools & Accessories', nameAr: 'الأدوات والإكسسوارات', icon: Wrench, color: 'bg-sky-500', items: 156 },
  { id: 'tires', name: 'Tires & Wheels', nameAr: 'الإطارات والجنوط', icon: CircleDot, color: 'bg-stone-500', items: 89 },
];

// Brand and model names mapping
const brandNames = {
  toyota: { name: 'Toyota', nameAr: 'تويوتا' },
  nissan: { name: 'Nissan', nameAr: 'نيسان' },
  mercedes: { name: 'Mercedes-Benz', nameAr: 'مرسيدس بنز' },
  bmw: { name: 'BMW', nameAr: 'بي إم دبليو' },
  lexus: { name: 'Lexus', nameAr: 'لكزس' },
  honda: { name: 'Honda', nameAr: 'هوندا' },
  hyundai: { name: 'Hyundai', nameAr: 'هيونداي' },
  kia: { name: 'Kia', nameAr: 'كيا' },
  ford: { name: 'Ford', nameAr: 'فورد' },
  chevrolet: { name: 'Chevrolet', nameAr: 'شيفروليه' },
  gmc: { name: 'GMC', nameAr: 'جي إم سي' },
  'land-rover': { name: 'Land Rover', nameAr: 'لاند روفر' },
  porsche: { name: 'Porsche', nameAr: 'بورش' },
  audi: { name: 'Audi', nameAr: 'أودي' },
};

const modelNames = {
  // Toyota
  camry: { name: 'Camry', nameAr: 'كامري' },
  corolla: { name: 'Corolla', nameAr: 'كورولا' },
  'land-cruiser': { name: 'Land Cruiser', nameAr: 'لاند كروزر' },
  prado: { name: 'Prado', nameAr: 'برادو' },
  rav4: { name: 'RAV4', nameAr: 'راف فور' },
  hilux: { name: 'Hilux', nameAr: 'هايلكس' },
  fortuner: { name: 'Fortuner', nameAr: 'فورتشنر' },
  yaris: { name: 'Yaris', nameAr: 'ياريس' },
  // Nissan
  patrol: { name: 'Patrol', nameAr: 'باترول' },
  altima: { name: 'Altima', nameAr: 'التيما' },
  maxima: { name: 'Maxima', nameAr: 'ماكسيما' },
  'x-trail': { name: 'X-Trail', nameAr: 'إكس تريل' },
  pathfinder: { name: 'Pathfinder', nameAr: 'باثفايندر' },
  sunny: { name: 'Sunny', nameAr: 'صني' },
  // Mercedes
  'c-class': { name: 'C-Class', nameAr: 'الفئة C' },
  'e-class': { name: 'E-Class', nameAr: 'الفئة E' },
  's-class': { name: 'S-Class', nameAr: 'الفئة S' },
  gle: { name: 'GLE', nameAr: 'GLE' },
  gls: { name: 'GLS', nameAr: 'GLS' },
  'g-class': { name: 'G-Class', nameAr: 'الفئة G' },
  // BMW
  '3-series': { name: '3 Series', nameAr: 'الفئة 3' },
  '5-series': { name: '5 Series', nameAr: 'الفئة 5' },
  '7-series': { name: '7 Series', nameAr: 'الفئة 7' },
  x5: { name: 'X5', nameAr: 'X5' },
  x7: { name: 'X7', nameAr: 'X7' },
  // Generic fallback
};

export default function CarPartsCategories() {
  const { brandId, modelId } = useParams();
  const navigate = useNavigate();
  const { isDarkMode } = useTheme();
  const { language } = useLanguage();
  const isAr = language === 'ar';
  
  const [searchQuery, setSearchQuery] = useState('');

  const brand = brandNames[brandId];
  const model = modelNames[modelId] || { name: modelId, nameAr: modelId };

  // Filter categories
  const filteredCategories = partsCategories.filter(cat => 
    cat.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    cat.nameAr.includes(searchQuery)
  );

  if (!brand) {
    return (
      <div className={`min-h-screen flex items-center justify-center ${isDarkMode ? 'bg-[#0a0a0a] text-white' : 'bg-gray-50 text-[#0f172a]'}`}>
        <div className="text-center">
          <Car className="h-16 w-16 mx-auto mb-4 text-gray-400" />
          <h2 className="text-2xl font-bold mb-2">{isAr ? 'غير موجود' : 'Not found'}</h2>
          <button onClick={() => navigate('/spare-parts')} className="text-orange-500 hover:underline">
            {isAr ? 'العودة للماركات' : 'Back to brands'}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={`min-h-screen ${isDarkMode ? 'bg-[#0a0a0a]' : 'bg-gray-50'}`}>
      {/* Hero Section */}
      <section className={`relative py-10 ${isDarkMode ? 'bg-gradient-to-b from-orange-500/20 to-transparent' : 'bg-gradient-to-b from-orange-500/10 to-transparent'}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Breadcrumb */}
          <div className="flex items-center gap-2 mb-6 text-sm flex-wrap">
            <button 
              onClick={() => navigate('/spare-parts')}
              className={`transition-colors ${isDarkMode ? 'text-gray-400 hover:text-white' : 'text-gray-600 hover:text-[#0f172a]'}`}
            >
              {isAr ? 'الماركات' : 'Brands'}
            </button>
            <span className={isDarkMode ? 'text-gray-600' : 'text-gray-400'}>/</span>
            <button 
              onClick={() => navigate(`/spare-parts/${brandId}`)}
              className={`transition-colors ${isDarkMode ? 'text-gray-400 hover:text-white' : 'text-gray-600 hover:text-[#0f172a]'}`}
            >
              {isAr ? brand.nameAr : brand.name}
            </button>
            <span className={isDarkMode ? 'text-gray-600' : 'text-gray-400'}>/</span>
            <span className={`font-medium ${isDarkMode ? 'text-white' : 'text-[#0f172a]'}`}>
              {isAr ? model.nameAr : model.name}
            </span>
          </div>

          <div className="flex items-center gap-4">
            <div className={`w-16 h-16 rounded-xl flex items-center justify-center ${isDarkMode ? 'bg-orange-500/20' : 'bg-orange-500/10'}`}>
              <Car className={`w-8 h-8 ${isDarkMode ? 'text-orange-400' : 'text-orange-500'}`} />
            </div>
            <div>
              <h1 className={`text-2xl sm:text-3xl font-bold ${isDarkMode ? 'text-white' : 'text-[#0f172a]'}`}>
                {isAr ? `قطع غيار ${brand.nameAr} ${model.nameAr}` : `${brand.name} ${model.name} Parts`}
              </h1>
              <p className={`mt-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                {isAr ? 'اختر قسم القطع المطلوب' : 'Select the parts category you need'}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Search */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div className={`rounded-2xl p-4 ${isDarkMode ? 'bg-[#141414] border border-white/10' : 'bg-white shadow-sm'}`}>
          <div className="relative">
            <Search className={`absolute ${isAr ? 'right-4' : 'left-4'} top-1/2 -translate-y-1/2 w-5 h-5 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`} />
            <input
              type="text"
              placeholder={isAr ? 'ابحث عن قسم...' : 'Search category...'}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className={`w-full ${isAr ? 'pr-12 pl-4' : 'pl-12 pr-4'} py-3 rounded-xl text-base transition-all ${isDarkMode ? 'bg-white/5 border border-white/10 text-white placeholder-gray-500 focus:border-orange-500/50' : 'bg-gray-50 border border-gray-200 text-[#0f172a] placeholder-gray-400 focus:border-orange-500'} focus:outline-none focus:ring-2 focus:ring-orange-500/20`}
              data-testid="search-category"
            />
          </div>
        </div>

        {/* Results count */}
        <div className={`mt-4 text-sm ${isDarkMode ? 'text-gray-500' : 'text-gray-500'}`}>
          {isAr ? `${filteredCategories.length} قسم` : `${filteredCategories.length} categories`}
        </div>
      </section>

      {/* Categories Grid */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-16">
        {filteredCategories.length === 0 ? (
          <div className={`text-center py-20 rounded-2xl ${isDarkMode ? 'bg-[#141414]' : 'bg-white'}`}>
            <Settings className={`h-16 w-16 mx-auto mb-4 ${isDarkMode ? 'text-gray-700' : 'text-gray-300'}`} />
            <p className={`text-xl font-medium mb-2 ${isDarkMode ? 'text-white' : 'text-[#0f172a]'}`}>
              {isAr ? 'لا توجد أقسام' : 'No categories found'}
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
            {filteredCategories.map((category) => {
              const IconComponent = category.icon;
              return (
                <Link
                  key={category.id}
                  to={`/spare-parts/${brandId}/${modelId}/${category.id}`}
                  className={`group rounded-2xl p-5 transition-all hover:scale-[1.02] ${isDarkMode ? 'bg-[#141414] border border-white/10 hover:border-orange-500/50 hover:shadow-lg hover:shadow-orange-500/10' : 'bg-white border border-gray-200 hover:border-orange-500/50 hover:shadow-lg'}`}
                  data-testid={`category-${category.id}`}
                >
                  {/* Icon */}
                  <div className={`w-14 h-14 rounded-xl ${category.color} flex items-center justify-center mb-4 transition-transform group-hover:scale-110`}>
                    <IconComponent className="w-7 h-7 text-white" />
                  </div>

                  {/* Name */}
                  <h3 className={`font-semibold mb-1 ${isDarkMode ? 'text-white' : 'text-[#0f172a]'}`}>
                    {isAr ? category.nameAr : category.name}
                  </h3>

                  {/* Items count */}
                  <p className={`text-sm ${isDarkMode ? 'text-gray-500' : 'text-gray-500'}`}>
                    {isAr ? `${category.items} قطعة` : `${category.items} items`}
                  </p>

                  {/* Arrow */}
                  <div className={`mt-3 flex items-center gap-1 text-sm font-medium text-orange-500 opacity-0 group-hover:opacity-100 transition-opacity`}>
                    <span>{isAr ? 'تصفح' : 'Browse'}</span>
                    {isAr ? <ChevronLeft className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                  </div>
                </Link>
              );
            })}
          </div>
        )}
      </section>
    </div>
  );
}
